import {TitleCasePipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, computed, inject, Signal} from '@angular/core';
import {FormGroup, ReactiveFormsModule, UntypedFormGroup} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {buildResourceDetailsFields} from '@app/resource-explorer/resource.form.config';
import {CategoryFilter} from '@app/search/filters/resources/category.filter';
import {prepareFormPayload} from '@forms/utils/form-utils';
import {ValidationErrorHandler} from '@forms/validationerrorhandler.service';
import {
  CategoryService,
  ContactBase,
  EventCreate,
  EventResponse,
  GenericResourceCreate,
  ResourceIconID,
  ResourceResponse,
  ResourceService,
} from '@middleware';
import {IconService} from '@middleware/services/icon/icon.service';
import {ResourcePermissionsFlags} from '@models/search';
import {FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyFormOptions, FormlyModule} from '@ngx-formly/core';
import {
  SectionHeaderAction,
  SectionHeaderActions,
} from '@shared/components/section-header-actions/section-header-actions';
import {formatPhoneNumber, stripPhoneNumber} from '@validators/phone-number.validator';
import {UUID} from 'crypto';
import dayjs from 'dayjs';
import {lastValueFrom, Subscription} from 'rxjs';

@Component({
  standalone: true,
  imports: [MatFormFieldModule],
  selector: 'formly-text-display-field',
  template: `<div class="form-field standard">
    <mat-label class="form-field standard required">{{ this.props.label }}</mat-label>
    <span class="display-field-value" [innerHTML]="this.props['value']()"></span>
    <input type="hidden" [value]="this.props['value']()" class="cdk-visually-hidden" />
  </div>`,
})
class TextDisplayField extends FieldType<FieldTypeConfig> {}

@Component({
  standalone: true,
  selector: 'app-resource-detail-editor',
  imports: [ReactiveFormsModule, FormlyModule, MatIconModule, MatButtonModule, RouterLink, TitleCasePipe],
  templateUrl: './resource-details.component.html',
  styleUrl: './resource-details.component.scss',
})
export class ResourceDetailsComponent implements SectionHeaderActions {
  public objectName: string = 'resource'; //for unsaved data guard
  protected creatorOrganization: string;
  __subscription: Subscription;
  form: FormGroup = new UntypedFormGroup({});
  formFields: FormlyFieldConfig[];
  id: UUID;
  model: GenericResourceCreate | EventCreate;
  modelType: 'Generic' | 'Event';
  options: FormlyFormOptions = {};
  submitting: boolean = false;
  private readonly categoryFilter = inject(CategoryFilter);

  constructor(
    private readonly categoryService: CategoryService,
    private readonly iconService: IconService,
    route: ActivatedRoute,
    private readonly router: Router,
    private readonly resourceService: ResourceService,
    private readonly snackbarService: MatSnackBar,
    private readonly validationErrorHandler: ValidationErrorHandler,
  ) {
    this.__subscription = route.data.subscribe(data => {
      const resource = data['resource'] as ResourceResponse & ResourcePermissionsFlags;
      this.id = <UUID>resource.id;
      this.objectName = resource.resource_type === 'Generic' ? 'resource' : resource.resource_type.toLowerCase();
      this.creatorOrganization = resource.creator?.organization?.full_name;
      this.model = ResourceDetailsComponent.modelMap(resource);
    });
    this.formFields = buildResourceDetailsFields({
      modelType: this.modelType,
      creatorOrganization: this.creatorOrganization,
      categoryService: this.categoryService,
      iconService: this.iconService,
      categoryFilter: this.categoryFilter,
    });
  }

  static modelMap(resource: ResourceResponse & ResourcePermissionsFlags): GenericResourceCreate | EventCreate {
    const res = <EventResponse>resource;
    return {
      resource_type: res.resource_type,

      name: res.name,
      icon_id: <ResourceIconID>res.icon.id,
      show_creator_organization_logo: res.show_creator_organization_logo,
      description: res.description,
      categories: res.categories.map(cat => cat.id),
      // Make sure contacts is always typed as an array of contact objects
      contacts: (Array.isArray(res.contacts)
        ? (res.contacts as ContactBase[])
        : res.contacts
          ? [res.contacts as ContactBase]
          : [{email: '', phone_number: '', name: ''}]
      ).map(c => ({...c, phone_number: c.phone_number ? formatPhoneNumber(c.phone_number) : c.phone_number})),
      website_url: res.website_url,

      spatial_coverage: res.spatial_coverage ?? {street: '', city: '', state: '', zip_code: '', country: ''},

      location: res.event_metadata?.location,
      //@ts-expect-error
      starts: res.event_metadata?.starts ? dayjs(res.event_metadata.starts) : undefined,
      //@ts-expect-error
      ends: res.event_metadata?.ends ? dayjs(res.event_metadata.ends) : undefined,

      owners: res.owners.map(owner => owner.email),
      target_audiences: res.target_audiences.map(org => org.name), //Backend specifically requests case-insensitive name instead of ID.
      visible_to_all_users: res.visible_to_all_users,
    };
  }

  async submit(event: Event): Promise<void> {
    event.preventDefault();
    if (this.form.status == 'PENDING') {
      this.snackbarService.open("We're making sure everything's alright, try again in a few seconds.", undefined, {
        duration: 5000,
      });
      return;
    } else if (!this.form.valid) {
      this.validationErrorHandler.handleError(this.formFields);
      return;
    }
    try {
      this.submitting = true;
      const sanitizedForm = await prepareFormPayload(this.model, this.formFields);
      if (sanitizedForm.contacts) {
        sanitizedForm.contacts = sanitizedForm.contacts.map((c: ContactBase) => ({
          ...c,
          phone_number: c.phone_number ? stripPhoneNumber(c.phone_number) : c.phone_number,
        }));
      }
      await lastValueFrom(this.resourceService.updateResource(this.id, sanitizedForm));

      this.form.markAsPristine();
      await this.router.navigate(['/resource-explorer/view', this.id]);
    } catch (e) {
      this.validationErrorHandler.handleError(this.formFields, <HttpErrorResponse>e);
    } finally {
      this.submitting = false;
    }
  }

  public sectionHeaderActions: Signal<SectionHeaderAction[]> = computed(() => {
    const actions: SectionHeaderAction[] = [
      {
        target: '/help/resource-explorer/edit-resource-details',
        targetTab: '_blank',
        caption: 'Help',
        color: 'var(--mat-sys-primary)',
        icon: 'help_outline',
      },
    ];

    return actions;
  });
}
